// Step 4 (superconducting calculations only): "Contacts & fields" - the superconductors (gap, phase, Tc, critical
// fields), which lead is superconducting and which device cells are proximitized, the g-tensor, the magnetic field
// (Cartesian, or |B| with the measurements' angles: φ from Z = the wire, θ from X towards Y), a gate on a range of
// cells, temperature and the charging energy. The device is drawn along the transport direction as it is set up.
import { ActionIcon, Alert, Button, Card, Checkbox, Grid, Group, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Table,
  Text, TextInput } from "@mantine/core";
import { useMemo, useState } from "react";
import { Plot } from "../../components/Plot";
import type { Limits, ProjectDetail } from "../../lib/api";
import type { NegfMemory } from "../../lib/negf";
import { bSpherical, contactMatrix, defaultContact, defaultSc, defaultTunnel, FIELD_AXES, G_ELECTRON, GAP_LAWS, responsePrincipal, scheme, scPreset,
  scProblems, toSpherical, type ContactForm, type FieldAxes, type GapLaw, type GMode, type LeadKind, type ScForm, type ScMethod, type ScPreset, type TunnelForm } from "../../lib/sc";

function Num({ label, value, onChange, unit, min, step, int = false, description, w }: {
  label?: string; value: number; onChange: (v: number) => void; unit?: string; min?: number; step?: number; int?: boolean;
  description?: string; w?: number;
}) {
  return <NumberInput label={label} description={description} size="xs" w={w} aria-label={label}
    rightSection={unit ? <Text size="xs" c="dimmed">{unit}</Text> : undefined} rightSectionWidth={unit ? 40 : undefined}
    value={value} min={min} step={step} allowDecimal={!int} decimalScale={int ? 0 : 6}
    onChange={(v) => onChange(typeof v === "number" ? v : Number(v) || 0)} />;
}

function OptNum({ label, value, onChange, unit, disabled }: { label: string; value: number | null; onChange: (v: number | null) => void; unit: string;
  disabled?: boolean }) {
  return (
    <NumberInput size="xs" aria-label={label} placeholder="none" value={value ?? ""} min={0} decimalScale={6} disabled={disabled}
      rightSection={<Text size="xs" c="dimmed">{unit}</Text>} rightSectionWidth={30}
      onChange={(v) => onChange(v === "" || v == null ? null : Number(v))} />
  );
}

const fmt = (v: number) => (Number.isFinite(v) ? (+v.toPrecision(3)).toString() : "∞");

/** The second row of a contact: the rotated critical-field axes or the 3×3 field-response matrix, with what it means. */
function ResponseRow({ c, i, set }: { c: ContactForm; i: number; set: (c: ContactForm) => void }) {
  const mode = c.field_axes ?? "wire";
  if (mode === "wire") return null;
  const M = contactMatrix(c);
  const pr = M ? responsePrincipal(M) : null;
  const ax = c.axes_deg ?? { tilt_deg: 0, azimuth_deg: 0, roll_deg: 0 };
  const R = c.response_T2 ?? [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  const setM = (a: number, b: number, v: number) =>
    set({ ...c, response_T2: R.map((row, r) => row.map((x, k) => ((r === a && k === b) || (r === b && k === a) ? v : x))) });
  return (
    <Table.Tr data-testid={`contact-${i}-response`}>
      <Table.Td />
      <Table.Td colSpan={7}>
        <Group gap="md" align="flex-start" wrap="wrap">
          {mode === "rotated" ? (
            <Group gap={4} wrap="nowrap" align="flex-end">
              <Num label="tilt from z" w={100} step={1} unit="°" value={ax.tilt_deg} onChange={(tilt_deg) => set({ ...c, axes_deg: { ...ax, tilt_deg } })} />
              <Num label="azimuth from x" w={100} step={5} unit="°" value={ax.azimuth_deg} onChange={(azimuth_deg) => set({ ...c, axes_deg: { ...ax, azimuth_deg } })} />
              <Num label="roll about z″" w={100} step={5} unit="°" value={ax.roll_deg} onChange={(roll_deg) => set({ ...c, axes_deg: { ...ax, roll_deg } })} />
            </Group>
          ) : (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">M (T⁻²), symmetric: r = Bᵀ M B</Text>
              {[0, 1, 2].map((a) => (
                <Group key={a} gap={2} wrap="nowrap">
                  {[0, 1, 2].map((b) => (
                    <NumberInput key={b} size="xs" w={80} aria-label={`contact ${i + 1} M ${"xyz"[a]}${"xyz"[b]}`} value={R[a][b]} decimalScale={6} step={0.05}
                      disabled={b < a} onChange={(v) => setM(a, b, typeof v === "number" ? v : Number(v) || 0)} />
                  ))}
                </Group>
              ))}
            </Stack>
          )}
          <Text size="xs" c="dimmed" maw={360}>
            {pr ? <>Principal critical fields {pr.Bc_T.map(fmt).join(", ")} T; the most robust axis is {fmt(pr.tilt_deg)}° from z, azimuth {fmt(pr.azimuth_deg)}°
              (in the wire's frame).</> : "Set the critical fields Bc for the rotated axes."}
          </Text>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function ContactRow({ c, i, set, remove }: { c: ContactForm; i: number; set: (c: ContactForm) => void; remove?: () => void }) {
  const bc = c.Bc_T;
  const mode = c.field_axes ?? "wire";
  return (
    <>
    <Table.Tr data-testid={`contact-${i}`}>
      <Table.Td><TextInput size="xs" value={c.name} aria-label={`contact ${i + 1} name`} onChange={(e) => set({ ...c, name: e.currentTarget.value })} /></Table.Td>
      <Table.Td><Num value={c.delta_meV} min={0} step={0.05} unit="meV" onChange={(delta_meV) => set({ ...c, delta_meV })} label={`contact ${i + 1} gap`} /></Table.Td>
      <Table.Td><OptNum label={`contact ${i + 1} Tc`} value={c.Tc_K} unit="K" onChange={(Tc_K) => set({ ...c, Tc_K })} /></Table.Td>
      <Table.Td>
        <Group gap={2} wrap="nowrap">
          {[0, 1, 2].map((a) => (
            <OptNum key={a} label={`contact ${i + 1} Bc ${"xyz"[a]}`} unit="T" value={bc ? bc[a] : null}
              onChange={(v) => set({ ...c, Bc_T: v == null ? null : [0, 1, 2].map((k) => (k === a ? v : bc?.[k] ?? v)) })} disabled={mode === "matrix"} />
          ))}
        </Group>
      </Table.Td>
      <Table.Td><Select size="xs" w={170} allowDeselect={false} aria-label={`contact ${i + 1} field axes`} value={mode} data={FIELD_AXES}
        onChange={(v) => {
          const m = (v ?? "wire") as FieldAxes;
          set({ ...c, field_axes: m, ...(m === "matrix" && !c.response_T2 ? { response_T2: contactMatrix({ ...c, field_axes: "wire" }) ?? [[1, 0, 0], [0, 1, 0], [0, 0, 1]] } : {}) });
        }} /></Table.Td>
      <Table.Td><Select size="xs" w={150} allowDeselect={false} aria-label={`contact ${i + 1} field law`} value={c.law ?? "gl"} data={GAP_LAWS}
        onChange={(v) => set({ ...c, law: (v ?? "gl") as GapLaw })} /></Table.Td>
      <Table.Td><Num value={c.phase_deg} step={15} unit="°" onChange={(phase_deg) => set({ ...c, phase_deg })} label={`contact ${i + 1} phase`} /></Table.Td>
      <Table.Td><Num value={c.mu_meV} step={10} unit="meV" onChange={(mu_meV) => set({ ...c, mu_meV })} label={`contact ${i + 1} mu`} /></Table.Td>
      <Table.Td>{remove && <ActionIcon variant="subtle" color="red" onClick={remove} aria-label={`remove contact ${i + 1}`}>✕</ActionIcon>}</Table.Td>
    </Table.Tr>
    <ResponseRow c={c} i={i} set={set} />
    </>
  );
}

function LeadSelect({ label, value, contacts, onChange }: { label: string; value: LeadKind; contacts: ContactForm[]; onChange: (v: LeadKind) => void }) {
  return (
    <Select size="xs" label={label} allowDeselect={false} value={String(value)}
      data={[{ value: "normal", label: "Normal (a probe)" }, ...contacts.map((c, i) => ({ value: String(i), label: `Superconducting: ${c.name || `contact ${i + 1}`}` }))]}
      onChange={(v) => onChange(v == null || v === "normal" ? "normal" : Number(v))} />
  );
}

function GTensor({ f, set, species }: { f: ScForm; set: (n: Partial<ScForm>) => void; species: string[] }) {
  const els = [...new Set(species)];
  const gel = Object.fromEntries(els.map((el) => [el, f.g_element[el] ?? [G_ELECTRON, G_ELECTRON, G_ELECTRON]]));
  return (
    <Stack gap={4}>
      <SegmentedControl size="xs" value={f.g_mode} onChange={(v) => set({ g_mode: v as GMode, g_element: gel })}
        data={[{ value: "isotropic", label: "g" }, { value: "diagonal", label: "gxx gyy gzz" }, { value: "tensor", label: "3×3" },
          ...(els.length > 1 ? [{ value: "element", label: "per element" }] : [])]} data-testid="g-mode" />
      {f.g_mode === "isotropic" && <Num label="g factor" step={0.5} value={f.g} onChange={(g) => set({ g })} />}
      {f.g_mode === "diagonal" && (
        <Group grow gap={4}>{[0, 1, 2].map((a) => <Num key={a} label={`g${"xyz"[a]}${"xyz"[a]}`} step={0.5} value={f.g_diag[a]}
          onChange={(v) => set({ g_diag: f.g_diag.map((x, k) => (k === a ? v : x)) })} />)}</Group>
      )}
      {f.g_mode === "tensor" && (
        <SimpleGrid cols={3} spacing={4}>
          {[0, 1, 2].flatMap((r) => [0, 1, 2].map((c) => (
            <Num key={`${r}${c}`} label={`g${"xyz"[r]}${"xyz"[c]}`} step={0.5} value={f.g_tensor[r][c]}
              onChange={(v) => set({ g_tensor: f.g_tensor.map((row, i) => row.map((x, j) => (i === r && j === c ? v : x))) })} />
          )))}
        </SimpleGrid>
      )}
      {f.g_mode === "element" && els.map((el) => (
        <Group key={el} grow gap={4} align="flex-end">
          <Text size="xs" fw={600} maw={30}>{el}</Text>
          {[0, 1, 2].map((a) => <Num key={a} label={`g${"xyz"[a]}${"xyz"[a]}`} step={0.5} value={gel[el][a]}
            onChange={(v) => set({ g_element: { ...gel, [el]: gel[el].map((x, k) => (k === a ? v : x)) } })} />)}
        </Group>
      ))}
      <Text size="xs" c="dimmed">Zeeman term (μB/2) σ·(g B) on every orbital (for spinor Wannier functions with the run's spin
        matrices). Effective g factors of wires are often far from 2 and anisotropic - give the measured or fitted ones.</Text>
    </Stack>
  );
}

function FieldInput({ f, set }: { f: ScForm; set: (n: Partial<ScForm>) => void }) {
  const [mode, setMode] = useState<"xyz" | "angles">("angles");
  const sph = toSpherical(f.B_T);
  return (
    <Stack gap={4}>
      <SegmentedControl size="xs" value={mode} onChange={(v) => setMode(v as "xyz" | "angles")}
        data={[{ value: "angles", label: "|B|, θ, φ" }, { value: "xyz", label: "Bx, By, Bz" }]} />
      {mode === "xyz" ? (
        <Group grow gap={4}>{[0, 1, 2].map((a) => <Num key={a} label={`B${"xyz"[a]}`} unit="T" step={0.1} value={f.B_T[a]}
          onChange={(v) => set({ B_T: f.B_T.map((x, k) => (k === a ? v : x)) })} />)}</Group>
      ) : (
        <Group grow gap={4}>
          <Num label="|B|" unit="T" min={0} step={0.1} value={+sph.mag.toFixed(6)} onChange={(m) => set({ B_T: bSpherical(m, sph.theta, sph.phi) })} />
          <Num label="θ (from X)" unit="°" step={15} value={sph.theta} onChange={(t) => set({ B_T: bSpherical(sph.mag, t, sph.phi) })} />
          <Num label="φ (from Z)" unit="°" step={15} value={sph.phi} onChange={(p) => set({ B_T: bSpherical(sph.mag, sph.theta, p) })} />
        </Group>
      )}
      <Text size="xs" c="dimmed">B = ({f.B_T.map((x) => x.toPrecision(4)).join(", ")}) T. φ is the polar angle from Z (the wire), θ the
        azimuth from X towards Y. A map step sweeps |B| or the angle; this field is used otherwise.</Text>
      <Checkbox size="xs" label="Orbital effect (Peierls phases)" checked={f.peierls} onChange={(e) => set({ peierls: e.currentTarget.checked })} />
      <Group grow gap={4}>
        <Num label="Wire tilt from Z" unit="°" min={0} step={5} value={f.orientation?.tilt_deg ?? 0}
          onChange={(tilt_deg) => set({ orientation: { azimuth_deg: f.orientation?.azimuth_deg ?? 0, tilt_deg } })} />
        <Num label="towards (azimuth from X)" unit="°" step={15} value={f.orientation?.azimuth_deg ?? 0}
          onChange={(azimuth_deg) => set({ orientation: { tilt_deg: f.orientation?.tilt_deg ?? 0, azimuth_deg } })} />
      </Group>
      <Text size="xs" c="dimmed">A wire not aligned with the magnet: its axis (the model's z) points {f.orientation?.tilt_deg ? `${f.orientation.tilt_deg}° from Z towards
        azimuth ${f.orientation.azimuth_deg}° (−90°: towards −Y)` : "along Z"}. Fields and angles stay the magnet's; the wire, its g-tensor and the
        contacts' critical fields see the field in the wire's frame.</Text>
    </Stack>
  );
}

function MethodCard({ f, set, length }: { f: ScForm; set: (n: Partial<ScForm>) => void; length: number }) {
  const method = f.method ?? "coherent";
  const t = f.tunnel ?? defaultTunnel();
  const setT = (n: Partial<TunnelForm>) => set({ tunnel: { ...t, ...n } });
  const contacts = f.contacts.map((c, i) => ({ value: String(i), label: c.name || `contact ${i + 1}` }));
  return (
    <Card withBorder p="xs" data-testid="sc-method">
      <Text size="sm" fw={600} mb={4}>How the conductance is measured</Text>
      <SegmentedControl size="xs" fullWidth value={method} onChange={(v) => set({ method: v as ScMethod })}
        data={[{ value: "coherent", label: "Coherent (tier 1): a normal probe lead" }, { value: "tunnel", label: "Tunnel spectroscopy (tier 2)" }]} />
      {method === "coherent" ? (
        <Text size="xs" c="dimmed" mt={4}>BdG scattering at any transparency: Andreev reflection into a normal lead (N–S, N–S–N). For an S–wire–S
          junction use tunnel spectroscopy, or the Andreev levels and supercurrent.</Text>
      ) : (
        <Stack gap={4} mt={4}>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={6}>
            <Select size="xs" label="Tunnelling superconductor" allowDeselect={false} data={contacts} value={String(t.probe_contact)}
              onChange={(v) => setT({ probe_contact: Number(v ?? 0) })} />
            <Num label="Dynes γ" unit="meV" min={0} step={0.005} value={t.gamma_meV} onChange={(gamma_meV) => setT({ gamma_meV })} />
            <Num label="G_N (normal state)" unit="2e²/h" min={0} step={0.05} value={t.G_N} onChange={(G_N) => setT({ G_N })} />
            <Select size="xs" label="The wire's states" allowDeselect={false} value={t.wire} onChange={(v) => setT({ wire: (v ?? "bdg") as TunnelForm["wire"] })}
              data={[{ value: "bdg", label: "from the model (BdG LDOS)" }, { value: "bcs", label: "a second BCS superconductor" }]} />
          </SimpleGrid>
          {t.wire === "bdg" ? (
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={6}>
              <Num label="tunnels into cell" int min={0} value={t.cells[0]} onChange={(v) => setT({ cells: [v, Math.max(v + 1, t.cells[1])] })} />
              <Num label="to (excl.)" int min={1} value={t.cells[1]} onChange={(v) => setT({ cells: [t.cells[0], v] })} />
              <Num label="energy step" unit="meV" min={0.0005} step={0.001} value={t.dE_meV} onChange={(dE_meV) => setT({ dE_meV })} />
              <Num label="extra broadening" unit="meV" min={0} step={0.005} value={t.wire_gamma_meV} onChange={(wire_gamma_meV) => setT({ wire_gamma_meV })} />
            </SimpleGrid>
          ) : (
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={6}>
              <Select size="xs" label="Second superconductor" allowDeselect={false} data={contacts} value={String(t.wire_contact)}
                onChange={(v) => setT({ wire_contact: Number(v ?? 0) })} />
              <Num label="its Dynes γ" unit="meV" min={0} step={0.005} value={t.wire_gamma_meV} onChange={(wire_gamma_meV) => setT({ wire_gamma_meV })} />
            </SimpleGrid>
          )}
          <Text size="xs" c="dimmed">dI/dV = G_N d/dV ∫ ρ_S(E − eV) n(E) [f(E − eV) − f(E)] dE: a BCS–Dynes contact tunnelling weakly into the wire (the
            Te/PdₓTe regime, G ≪ 2e²/h). n(E) is the wire's density of states on the chosen cells (the BdG model with its leads, regions,
            field and gate; {length} cells), normalized to the normal state - or a second BCS superconductor (S–I–S: edge at Δ₁ + Δ₂).
            Both leads may be superconducting here.</Text>
        </Stack>
      )}
    </Card>
  );
}

export function ContactsStep({ mem, update, remember, onBack, onNext }: {
  project: ProjectDetail; mem: NegfMemory; update: (m: Partial<NegfMemory>) => void; remember: (m: Partial<NegfMemory>) => void;
  limits: Limits; onBack: () => void; onNext: () => void;
}) {
  const species = mem.summary?.species ?? [];
  const f = mem.sc ?? defaultSc(species);
  const set = (next: Partial<ScForm>) => update({ sc: { ...f, ...next } });
  const length = mem.device.length;
  const fig = useMemo(() => scheme(f, length), [f, length]);
  const problems = scProblems(f, length);
  const setContact = (i: number, c: ContactForm) => set({ contacts: f.contacts.map((x, k) => (k === i ? c : x)) });
  const removeContact = (i: number) => {
    const fix = (v: LeadKind): LeadKind => (v === "normal" ? v : v === i ? "normal" : v > i ? v - 1 : v);
    set({ contacts: f.contacts.filter((_, k) => k !== i), left: fix(f.left), right: fix(f.right),
      regions: f.regions.filter((r) => r.contact !== i).map((r) => ({ ...r, contact: r.contact > i ? r.contact - 1 : r.contact })) });
  };
  return (
    <Grid mt="md" gutter="lg">
      <Grid.Col span={{ base: 12, md: 7 }}>
        <Stack gap="sm">
          <Group gap="xs">
            <Text size="sm" fw={600}>Set-ups:</Text>
            {([["ns", "N–S junction"], ["nsn", "N–S region–N"], ["wire", "Wire on a superconductor"], ["sns", "S–N–S (Andreev levels)"],
              ["te", "Te / PdₓTe tunnel junction"]] as [ScPreset, string][]).map(([k, label]) => (
              <Button key={k} size="compact-xs" variant="light" onClick={() => update({ sc: scPreset(k, length, f) })}>{label}</Button>
            ))}
          </Group>
          <Card withBorder p="xs">
            <Text size="sm" fw={600} mb={4}>Superconductors</Text>
            <Table.ScrollContainer minWidth={820}>
              <Table fz="xs" verticalSpacing={2}>
                <Table.Thead><Table.Tr><Table.Th>Name</Table.Th><Table.Th>Δ (T = 0, B = 0)</Table.Th><Table.Th>Tc</Table.Th>
                  <Table.Th>Bc x, y, z</Table.Th><Table.Th>Field axes</Table.Th><Table.Th>Δ(B) law</Table.Th><Table.Th>Phase</Table.Th><Table.Th>μ shift</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {f.contacts.map((c, i) => <ContactRow key={i} c={c} i={i} set={(x) => setContact(i, x)}
                    remove={f.contacts.length > 1 ? () => removeContact(i) : undefined} />)}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="space-between" mt={4}>
              <Button size="compact-xs" variant="subtle" onClick={() => set({ contacts: [...f.contacts, defaultContact(`S${f.contacts.length + 1}`)] })}>+ superconductor</Button>
              <Text size="xs" c="dimmed">Δ(B, T) = Δ·tanh(1.74√(Tc/T − 1))·f(r), r = Bᵀ M B in the wire's frame: M = diag(1/Bc²) for Bc along x, y, z,
                R diag(1/Bc²) Rᵀ for Bc along axes rotated by tilt, azimuth and roll, or a 3×3 M; f = √(1 − r) or 1 − r (pair
                breaking: a gap that closes gradually); empty Tc / Bc: no dependence.</Text>
            </Group>
          </Card>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
            <LeadSelect label="Left lead" value={f.left} contacts={f.contacts} onChange={(left) => set({ left })} />
            <LeadSelect label="Right lead" value={f.right} contacts={f.contacts} onChange={(right) => set({ right })} />
            <Select size="xs" label="Conductance measured in" allowDeselect={false} value={f.probe == null ? "auto" : String(f.probe)}
              data={[{ value: "auto", label: "the first normal lead" }, { value: "0", label: "the left lead" }, { value: "1", label: "the right lead" }]}
              onChange={(v) => set({ probe: v === "0" ? 0 : v === "1" ? 1 : null })} />
          </SimpleGrid>
          <Card withBorder p="xs">
            <Group justify="space-between"><Text size="sm" fw={600}>Proximitized regions of the device</Text>
              <Button size="compact-xs" variant="subtle" onClick={() => set({ regions: [...f.regions, { cells: [0, length], contact: 0 }] })}>+ region</Button></Group>
            {!f.regions.length && <Text size="xs" c="dimmed">None: the device is normal (a superconducting lead can still be the contact).</Text>}
            {f.regions.map((r, i) => (
              <Group key={i} gap={6} align="flex-end" data-testid={`region-${i}`}>
                <Num label="from cell" int min={0} w={90} value={r.cells[0]} onChange={(v) => set({ regions: f.regions.map((x, k) => (k === i ? { ...x, cells: [v, x.cells[1]] } : x)) })} />
                <Num label="to (excl.)" int min={1} w={90} value={r.cells[1]} onChange={(v) => set({ regions: f.regions.map((x, k) => (k === i ? { ...x, cells: [x.cells[0], v] } : x)) })} />
                <Select size="xs" label="contact" w={160} allowDeselect={false} value={String(r.contact)}
                  data={f.contacts.map((c, k) => ({ value: String(k), label: c.name || `contact ${k + 1}` }))}
                  onChange={(v) => set({ regions: f.regions.map((x, k) => (k === i ? { ...x, contact: Number(v ?? 0) } : x)) })} />
                <ActionIcon variant="subtle" color="red" onClick={() => set({ regions: f.regions.filter((_, k) => k !== i) })} aria-label={`remove region ${i + 1}`}>✕</ActionIcon>
              </Group>
            ))}
          </Card>
          <MethodCard f={f} set={set} length={length} />
          <Grid gutter="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Card withBorder p="xs"><Text size="sm" fw={600} mb={4}>Magnetic field</Text><FieldInput f={f} set={set} /></Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Card withBorder p="xs"><Text size="sm" fw={600} mb={4}>g-tensor</Text><GTensor f={f} set={set} species={species} /></Card>
            </Grid.Col>
          </Grid>
          <Card withBorder p="xs">
            <Text size="sm" fw={600} mb={4}>Gate, chemical potential, temperature, charging energy</Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={6}>
              <Num label="Gate potential" unit="meV" step={10} value={f.gate.V_meV} onChange={(V_meV) => set({ gate: { ...f.gate, V_meV } })} />
              <Checkbox size="xs" mt={22} label="on the whole device" checked={!f.gate.cells}
                onChange={(e) => set({ gate: { ...f.gate, cells: e.currentTarget.checked ? null : [Math.min(1, length - 1), Math.min(2, length)] } })} />
              {f.gate.cells && <Num label="from cell" int min={0} value={f.gate.cells[0]} onChange={(v) => set({ gate: { ...f.gate, cells: [v, f.gate.cells![1]] } })} />}
              {f.gate.cells && <Num label="to (excl.)" int min={1} value={f.gate.cells[1]} onChange={(v) => set({ gate: { ...f.gate, cells: [f.gate.cells![0], v] } })} />}
              <Num label="μ (everywhere)" unit="meV" step={10} value={f.mu_meV} onChange={(mu_meV) => set({ mu_meV })} />
              <Num label="Temperature" unit="K" min={0} step={0.05} value={f.temperature_K} onChange={(temperature_K) => set({ temperature_K })} />
              <Num label="Charging energy E_C" unit="meV" min={0} step={0.05} value={f.charging_energy_meV}
                onChange={(charging_energy_meV) => set({ charging_energy_meV })} />
            </SimpleGrid>
            <Text size="xs" c="dimmed" mt={4}>The gate adds its potential (meV; positive raises the energies, a barrier) to the chosen cells
              - a tunnel barrier next to the probe, or a top gate. The temperature sets Δ(T) and smears G(V). The charging energy is
              kept with each run; the coherent (BdG scattering) calculations do not use it - Coulomb blockade comes with the rate
              equations of a later phase.</Text>
          </Card>
          {problems.map((p) => <Text key={p} size="sm" c="red">{p}</Text>)}
          <Group justify="space-between">
            <Button variant="default" onClick={onBack}>Back</Button>
            <Group gap="xs">
              <Button variant="subtle" onClick={onNext}>Skip (no superconductors)</Button>
              <Button disabled={problems.length > 0} onClick={() => { remember({ sc: f }); onNext(); }}>Next: calculation</Button>
            </Group>
          </Group>
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 5 }}>
        <Stack gap="sm">
          <Plot data={fig.data} layout={fig.layout} height={260} />
          <Alert color="grape" p="xs">
            <Text size="sm">Bogoliubov–de Gennes on the model of step 1 (electrons and holes; the device has twice the orbitals).
              The calculations “G(V) · SC” and “G(V, X) map · SC” measure the differential conductance in the probe lead
              (Andreev reflection), in units of 2e²/h, against the bias, the field (along an axis or rotated in the XY, XZ
              or YZ plane), a gate or μ.</Text>
          </Alert>
          <Text size="xs" c="dimmed">Topological or trivial: with spin–orbit coupling and a Zeeman energy g μB B / 2 above Δ (μ near a
            band bottom) a wire on a superconductor becomes topological - a zero-bias peak of 2e²/h at the probe. Ask for
            the wire's BdG gap in a map to see where it closes and reopens.</Text>
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
